// Diagnosis Service
// Complete CRUD operations for Diagnoses
// Part of Diagnosis Nomenclature Implementation

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import whoIcdApiService from './whoIcdApiService';

class DiagnosisService {
  constructor() {
    this.collectionName = 'diagnoses';
    this.collectionRef = collection(db, this.collectionName);
  }

  /**
   * Create new diagnosis
   */
  async createDiagnosis(data, userId, userName) {
    try {
      // Validation
      if (!data.organizationId) {
        throw new Error('Organization ID is required');
      }
      if (!data.icd10Code) {
        throw new Error('ICD-10 code is required');
      }
      if (!data.commonName) {
        throw new Error('Common name is required');
      }
      if (!data.category) {
        throw new Error('Category is required');
      }

      // Validate ICD-10 code with WHO API (optional, non-blocking)
      if (data.icd10Code && whoIcdApiService.isApiAvailable()) {
        try {
          const isValid = await whoIcdApiService.validateCode(data.icd10Code);
          if (!isValid) {
            console.warn(`ICD-10 code ${data.icd10Code} validation failed, but continuing...`);
          }
        } catch (error) {
          // API error - continue without validation
          console.warn('WHO API validation failed, continuing without validation:', error);
        }
      }

      // Sync with WHO API if code provided (optional, non-blocking)
      let whoApiData = null;
      if (data.icd10Code && whoIcdApiService.isApiAvailable()) {
        try {
          whoApiData = await whoIcdApiService.syncDiagnosis(data.icd10Code);
        } catch (error) {
          // API error - continue without sync
          console.warn('WHO API sync failed, continuing without sync:', error);
        }
      }

      const now = Timestamp.now();

      const diagnosisData = {
        ...data,
        whoApiSynced: whoApiData !== null,
        whoApiSyncDate: whoApiData ? now : null,
        whoApiData: whoApiData,
        isActive: data.isActive !== undefined ? data.isActive : true,
        displayOrder: data.displayOrder || 0,
        // Ensure arrays exist
        causativePathogens: data.causativePathogens || [],
        commonPathogenNames: data.commonPathogenNames || [],
        increasesSusceptibility: data.increasesSusceptibility || [],
        affectedSystems: data.affectedSystems || [],
        linkedProtocols: data.linkedProtocols || [],
        linkedRisks: data.linkedRisks || [],
        clinicalPresentation: data.clinicalPresentation || {
          symptoms: [],
          typicalCourse: '',
          diagnosticCriteria: []
        },
        createdAt: now,
        createdBy: userId,
        createdByName: userName || userId,
        updatedAt: now,
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(diagnosisData);

      const docRef = await addDoc(this.collectionRef, cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating diagnosis:', error);
      throw error;
    }
  }

  /**
   * Get all diagnoses
   */
  async getDiagnoses(organizationId, filters = {}) {
    try {
      if (!organizationId) {
        return [];
      }

      let q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId)
      );

      if (filters.activeOnly !== false) {
        q = query(q, where('isActive', '==', true));
      }

      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters.haiType) {
        q = query(q, where('haiType', '==', filters.haiType));
      }

      if (filters.ipcRelevant) {
        q = query(q, where('ipcRelevant', '==', true));
      }

      if (filters.isHAI) {
        q = query(q, where('isHAI', '==', true));
      }

      // Order by displayOrder
      q = query(q, orderBy('displayOrder', 'asc'));

      const snapshot = await getDocs(q);
      let diagnoses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Secondary sort by commonName
      diagnoses.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        }
        return (a.commonName || '').localeCompare(b.commonName || '');
      });

      return diagnoses;
    } catch (error) {
      console.error('Error getting diagnoses:', error);
      throw error;
    }
  }

  /**
   * Get single diagnosis
   */
  async getDiagnosis(diagnosisId) {
    try {
      const docRef = doc(this.collectionRef, diagnosisId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Diagnosis not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error getting diagnosis:', error);
      throw error;
    }
  }

  /**
   * Update diagnosis
   */
  async updateDiagnosis(diagnosisId, updates, userId, userName) {
    try {
      const docRef = doc(this.collectionRef, diagnosisId);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // If ICD-10 code changed, try to sync with WHO API
      if (updates.icd10Code && whoIcdApiService.isApiAvailable()) {
        try {
          const whoApiData = await whoIcdApiService.syncDiagnosis(updates.icd10Code);
          if (whoApiData) {
            updateData.whoApiSynced = true;
            updateData.whoApiSyncDate = Timestamp.now();
            updateData.whoApiData = whoApiData;
          }
        } catch (error) {
          console.warn('WHO API sync failed during update:', error);
        }
      }

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(updateData);

      await updateDoc(docRef, cleanedData);
      return diagnosisId;
    } catch (error) {
      console.error('Error updating diagnosis:', error);
      throw error;
    }
  }

  /**
   * Delete diagnosis
   */
  async deleteDiagnosis(diagnosisId) {
    try {
      const docRef = doc(this.collectionRef, diagnosisId);
      await deleteDoc(docRef);
      return diagnosisId;
    } catch (error) {
      console.error('Error deleting diagnosis:', error);
      throw error;
    }
  }

  /**
   * Search diagnoses using WHO API
   */
  async searchDiagnoses(searchTerm, organizationId) {
    try {
      // First, search local cache
      const localResults = await this.getDiagnoses(organizationId, {
        activeOnly: true
      });

      const localMatches = localResults.filter(diagnosis =>
        diagnosis.commonName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        diagnosis.icd10Code?.includes(searchTerm) ||
        diagnosis.icd10Description?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Then, search WHO API if available
      let whoApiResults = [];
      if (whoIcdApiService.isApiAvailable()) {
        try {
          const whoApiData = await whoIcdApiService.searchCodes(searchTerm);
          if (whoApiData.destinationEntities) {
            whoApiResults = whoApiData.destinationEntities.map(entity => {
              // WHO API uses @value for localized strings (JSON-LD format)
              const title = entity.title?.['@value'] || entity.title || entity.label || '';
              const code = entity.code || entity['@id'] || '';
              
              return {
                icd11Code: code,
                icd11Description: title,
                whoApiData: entity
              };
            });
          }
        } catch (error) {
          console.warn('WHO API search failed, using local results only:', error);
        }
      }

      return {
        local: localMatches,
        whoApi: whoApiResults,
        total: localMatches.length + whoApiResults.length
      };
    } catch (error) {
      console.error('Error searching diagnoses:', error);
      throw error;
    }
  }

  /**
   * Link diagnosis to biological entity
   */
  async linkBiologicalEntity(diagnosisId, biologicalEntityId, biologicalEntityName) {
    try {
      const diagnosisRef = doc(this.collectionRef, diagnosisId);
      const diagnosis = await getDoc(diagnosisRef);
      
      if (!diagnosis.exists()) {
        throw new Error('Diagnosis not found');
      }

      const currentData = diagnosis.data();
      const causativePathogens = currentData.causativePathogens || [];
      const commonPathogenNames = currentData.commonPathogenNames || [];
      
      if (!causativePathogens.includes(biologicalEntityId)) {
        causativePathogens.push(biologicalEntityId);
        if (biologicalEntityName && !commonPathogenNames.includes(biologicalEntityName)) {
          commonPathogenNames.push(biologicalEntityName);
        }
        
        await updateDoc(diagnosisRef, {
          causativePathogens,
          commonPathogenNames,
          updatedAt: Timestamp.now()
        });
      }

      return true;
    } catch (error) {
      console.error('Error linking biological entity:', error);
      throw error;
    }
  }

  /**
   * Unlink diagnosis from biological entity
   */
  async unlinkBiologicalEntity(diagnosisId, biologicalEntityId, biologicalEntityName) {
    try {
      const diagnosisRef = doc(this.collectionRef, diagnosisId);
      const diagnosis = await getDoc(diagnosisRef);
      
      if (!diagnosis.exists()) {
        throw new Error('Diagnosis not found');
      }

      const currentData = diagnosis.data();
      const causativePathogens = (currentData.causativePathogens || []).filter(
        id => id !== biologicalEntityId
      );
      const commonPathogenNames = (currentData.commonPathogenNames || []).filter(
        name => name !== biologicalEntityName
      );
      
      await updateDoc(diagnosisRef, {
        causativePathogens,
        commonPathogenNames,
        updatedAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Error unlinking biological entity:', error);
      throw error;
    }
  }

  /**
   * Get diagnoses by biological entity
   */
  async getDiagnosesByBiologicalEntity(organizationId, biologicalEntityId) {
    try {
      const allDiagnoses = await this.getDiagnoses(organizationId, {
        activeOnly: true
      });

      return allDiagnoses.filter(diagnosis =>
        (diagnosis.causativePathogens || []).includes(biologicalEntityId)
      );
    } catch (error) {
      console.error('Error getting diagnoses by biological entity:', error);
      throw error;
    }
  }

  /**
   * Get HAI diagnoses
   */
  async getHAIDiagnoses(organizationId, haiType = null) {
    try {
      const filters = {
        activeOnly: true,
        isHAI: true
      };

      if (haiType) {
        filters.haiType = haiType;
      }

      return await this.getDiagnoses(organizationId, filters);
    } catch (error) {
      console.error('Error getting HAI diagnoses:', error);
      throw error;
    }
  }

  /**
   * Remove undefined values helper
   */
  removeUndefinedValues(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
}

export default new DiagnosisService();

